import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { notasEnvioApi, remitosApi } from '../api/remitos'
import { fmtFecha, fmtFechaHora } from '../utils/fecha'
import EstadoVigenteBadge from '../components/EstadoVigenteBadge'
import Boton from '../components/Boton'

const EST_CFG = {
  entregado:    { bg: '#dcfce7', border: '#86efac', color: '#166534', label: 'Entregado' },
  no_entregado: { bg: '#fee2e2', border: '#fca5a5', color: '#991b1b', label: 'No entregado' },
  reagendado:   { bg: '#e0e7ff', border: '#a5b4fc', color: '#3730a3', label: 'Reagendado' },
  pendiente:    { bg: '#f1f5f9', border: '#e2e8f0', color: '#64748b', label: 'En reparto' },
}

function EntregaCard({ e, onImg, numero }) {
  const cfg = EST_CFG[e.estado_entrega] || EST_CFG.pendiente
  return (
    <div style={{ border: `1.5px solid ${cfg.border}`, borderRadius: 12, background: cfg.bg, padding: '16px 20px', marginBottom: 12 }}>
      {/* Estado + fecha/hora — lo más importante arriba */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
        <span style={{ fontSize: '1.1rem', fontWeight: 800, color: cfg.color }}>{cfg.label}</span>
        {e.entregado_at && (
          <span style={{ fontSize: '0.9rem', fontWeight: 700, color: cfg.color }}>
            {fmtFechaHora(e.entregado_at)}
          </span>
        )}
      </div>

      {/* Recibió — lo segundo más importante */}
      {e.receptor_nombre && (
        <div style={{ marginBottom: 10, padding: '8px 12px', background: 'rgba(255,255,255,0.7)', borderRadius: 8 }}>
          <span style={{ fontSize: '0.72rem', fontWeight: 700, color: cfg.color, textTransform: 'uppercase', display: 'block', marginBottom: 2 }}>Recibió</span>
          <span style={{ fontSize: '1rem', fontWeight: 700, color: '#1e293b' }}>{e.receptor_nombre}</span>
        </div>
      )}

      {/* Motivo no entrega */}
      {e.motivo_no_entrega && (
        <div style={{ marginBottom: 10, padding: '8px 12px', background: 'rgba(255,255,255,0.7)', borderRadius: 8 }}>
          <span style={{ fontSize: '0.72rem', fontWeight: 700, color: cfg.color, textTransform: 'uppercase', display: 'block', marginBottom: 2 }}>Motivo</span>
          <span style={{ fontSize: '0.9rem', color: '#991b1b', fontWeight: 600 }}>{e.motivo_no_entrega}</span>
        </div>
      )}

      {/* Reagendado */}
      {e.nueva_fecha && (
        <div style={{ marginBottom: 10, padding: '8px 12px', background: 'rgba(255,255,255,0.7)', borderRadius: 8 }}>
          <span style={{ fontSize: '0.72rem', fontWeight: 700, color: cfg.color, textTransform: 'uppercase', display: 'block', marginBottom: 2 }}>Reagendado para</span>
          <span style={{ fontSize: '0.9rem', fontWeight: 700, color: '#3730a3' }}>{fmtFecha(e.nueva_fecha)}</span>
        </div>
      )}

      {/* Fletero + salida */}
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 4, fontSize: '0.8125rem', color: cfg.color }}>
        {e.salida?.fletero && (
          <span>🚚 <strong>{e.salida.fletero.nombre}</strong>
            {e.salida.fletero.vehiculo && ` · ${e.salida.fletero.vehiculo}`}
            {e.salida.fletero.telefono && ` · 📞 ${e.salida.fletero.telefono}`}
          </span>
        )}
        {e.salida && (
          <span style={{ marginLeft: 'auto' }}>Salida #{e.salida.id}</span>
        )}
      </div>

      {/* Notas */}
      {e.notas && (
        <p style={{ margin: '8px 0 0', fontSize: '0.8rem', fontStyle: 'italic', color: '#64748b' }}>{e.notas}</p>
      )}

      {/* Fotos */}
      {e.imagenes?.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <p style={{ margin: '0 0 6px', fontSize: '0.72rem', fontWeight: 700, color: cfg.color, textTransform: 'uppercase' }}>
            Fotos ({e.imagenes.length})
          </p>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {e.imagenes.map((img, j) => (
              <img key={j} src={img.data} alt={`Foto ${j+1}`}
                style={{ width: 90, height: 90, objectFit: 'cover', borderRadius: 8, cursor: 'zoom-in', border: '2px solid rgba(255,255,255,0.8)' }}
                onClick={() => onImg(img.data)} />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default function LogisticaNeDetallePage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [ne, setNe]             = useState(null)
  const [entregas, setEntregas] = useState([])
  const [loading, setLoading]   = useState(true)
  const [imgModal, setImgModal]     = useState(null)
  const [reasignando, setReasignando] = useState(false)

  useEffect(() => {
    Promise.allSettled([notasEnvioApi.obtener(id), notasEnvioApi.entregas(id)])
      .then(([neResult, eResult]) => {
        if (neResult.status === 'fulfilled') setNe(neResult.value.data)
        if (eResult.status === 'fulfilled') setEntregas(eResult.value.data)
      })
      .finally(() => setLoading(false))
  }, [id])

  if (loading) return <p style={{ color: '#94a3b8', padding: 20 }}>Cargando...</p>
  if (!ne)     return <p style={{ color: '#ef4444', padding: 20 }}>Nota de envío no encontrada.</p>

  const dest    = ne.destinatario
  const remito  = ne.remito
  const arts    = ne.articulos || []
  const dir     = [dest?.calle, dest?.localidad, dest?.provincia].filter(Boolean).join(', ')

  return (
    <div style={{ maxWidth: 720, margin: '0 auto' }}>
      {imgModal && (
        <div onClick={() => setImgModal(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, cursor: 'zoom-out' }}>
          <img src={imgModal} alt="Foto" style={{ maxWidth: '92vw', maxHeight: '92vh', borderRadius: 10 }} />
        </div>
      )}

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20, gap: 12, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Boton variante="ghost" sm onClick={() => navigate('/logistica/remitos')}>←</Boton>
          <div>
            <h1 style={st.h1}>NE <code style={st.mono}>{ne.numero_ne}</code></h1>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 4 }}>
              {dest?.nombre && <span style={st.sub}>{dest.nombre}</span>}
              <EstadoVigenteBadge estadoVigente={ne.estado_vigente} />
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Boton variante="secondary" sm onClick={() => navigate(`/notas-envio/${id}`)}>
            Info de entrega →
          </Boton>
          {remito && (
            <Boton variante="secondary" sm onClick={() => navigate(`/logistica/remitos/${remito.id}`)}>
              Remito T80 {remito.numero_presea} →
            </Boton>
          )}
          {ne.estado_vigente?.codigo === 'fallo_sin_reasignar' && (
            <Boton
              variante="secondary"
              sm
              disabled={reasignando}
              onClick={async () => {
                if (!window.confirm('¿Volver a poner esta NE como disponible para asignar?')) return
                setReasignando(true)
                try {
                  await notasEnvioApi.liberar(id)
                  navigate('/asignaciones')
                } catch (err) {
                  alert(err.response?.data?.message || 'Error al reasignar.')
                  setReasignando(false)
                }
              }}
            >
              {reasignando ? '...' : 'Volver a asignar'}
            </Boton>
          )}
        </div>
      </div>

      {/* ─── HISTORIAL DE ENTREGAS — PRIMERO ─── */}
      <div style={st.section}>
        <h2 style={st.sectionTitle}>
          {entregas.length === 0
            ? 'Sin registros de entrega'
            : entregas.length === 1
              ? 'Resultado de la entrega'
              : `Historial de intentos (${entregas.length})`}
        </h2>
        {entregas.length === 0
          ? <p style={st.empty}>Todavía no hay intentos de entrega registrados para esta NE.</p>
          : entregas.map((e, i) => (
              <EntregaCard key={i} e={e} onImg={setImgModal} numero={ne.numero_ne} />
            ))
        }
      </div>

      {/* ─── DATOS DE LA NE ─── */}
      <div style={st.section}>
        <h2 style={st.sectionTitle}>Datos del envío</h2>

        {dest && (
          <div style={st.infoCard}>
            <p style={st.lbl}>Destinatario</p>
            <p style={{ ...st.val, fontSize: '1rem', fontWeight: 700 }}>{dest.nombre}</p>
            {dir && <p style={st.dirVal}>📍 {dir}</p>}
            {dest.codigo_postal && <span style={st.chipCp}>{dest.codigo_postal}</span>}
            {dest.telefono && <p style={st.sub2}>📞 {dest.telefono}</p>}
            {dest.notas && <p style={st.obs}>💬 {dest.notas}</p>}
          </div>
        )}

        <div style={st.grid3}>
          {ne.zona?.nombre && <div><p style={st.lbl}>Zona</p><p style={st.val}>{ne.zona.nombre}</p></div>}
          {ne.fecha_entrega_est && <div><p style={st.lbl}>Fecha estimada</p><p style={st.val}>{fmtFecha(ne.fecha_entrega_est)}</p></div>}
          {(ne.hora_desde || ne.hora_hasta) && <div><p style={st.lbl}>Horario</p><p style={st.val}>{ne.hora_desde} – {ne.hora_hasta}</p></div>}
          {ne.instrucciones_cli && <div><p style={st.lbl}>🛎️ Cliente</p><p style={{ ...st.val, color: '#0f766e', fontWeight: 600 }}>{ne.instrucciones_cli}</p></div>}
        </div>

        {arts.length > 0 && (
          <div style={{ marginTop: 12 }}>
            <p style={st.lbl}>Artículos ({arts.reduce((s, a) => s + Number(a.cantidad), 0)} unid.)</p>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
              {arts.map(a => (
                <span key={a.id} style={st.artChip}>
                  <code style={{ fontWeight: 800, fontSize: '0.7rem' }}>{a.articulo?.codigo_alfa}</code>
                  <span style={{ color: '#475569', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.articulo?.descripcion}</span>
                  <strong style={{ color: '#1e40af' }}>× {a.cantidad}</strong>
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

const st = {
  h1:          { fontSize: '1.3rem', fontWeight: 700, color: '#1e293b', margin: 0 },
  mono:        { fontFamily: 'monospace', background: '#f1f5f9', padding: '2px 7px', borderRadius: 4 },
  sub:         { fontSize: '0.875rem', color: '#475569', fontWeight: 600 },
  section:     { background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 1px 4px rgba(0,0,0,.06)', marginBottom: 16 },
  sectionTitle:{ fontSize: '0.85rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', margin: '0 0 14px', letterSpacing: '0.04em' },
  empty:       { color: '#94a3b8', fontSize: '0.875rem' },
  infoCard:    { background: '#f8fafc', borderRadius: 8, padding: '12px 14px', marginBottom: 12 },
  lbl:         { margin: '0 0 2px', fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' },
  val:         { margin: '0 0 4px', fontSize: '0.875rem', color: '#1e293b', fontWeight: 500 },
  dirVal:      { margin: '4px 0 2px', fontSize: '0.85rem', color: '#475569' },
  sub2:        { margin: '4px 0 0', fontSize: '0.8rem', color: '#64748b' },
  obs:         { margin: '6px 0 0', fontSize: '0.8rem', color: '#92400e', background: '#fefce8', padding: '4px 8px', borderRadius: 5 },
  chipCp:      { fontSize: '0.72rem', fontWeight: 700, color: '#1e40af', background: '#eff6ff', padding: '2px 7px', borderRadius: 6, border: '1px solid #bfdbfe', fontFamily: 'monospace', display: 'inline-block', marginTop: 4 },
  grid3:       { display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12 },
  artChip:     { display: 'inline-flex', alignItems: 'center', gap: 6, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, padding: '3px 8px', fontSize: '0.78rem' },
}
